// SubViewRouter — Presentational routing for the EditTripModal's full-screen
// sub-views. When one of the sub-view flags is active the modal renders one of
// the child forms *instead of* the main editor body, exactly as the source did;
// otherwise it renders nothing (null) and the container renders the main modal.
//
// Extracted verbatim from `EditTripModal.js` (the `getSubView` helper) as part
// of the behavior-preserving refactor (issue #240). The child components
// AddMeetingForm, AddEventForm, and TripPDFUpdater are NOT reimplemented — they
// are imported from their existing module paths (`../../AddMeetingForm`,
// `../../AddEventForm`, `../../TripPDFUpdater`) and rendered with the identical
// props, in the identical priority order (add-meeting → edit-meeting →
// pdf-updater → add-event), that the source used. The source's inline cancel
// handlers (which flipped local state) are surfaced as `onCancel*` callbacks so
// this component stays render-only. Wrapped in React.memo.
//
// Returns the active sub-view element, or null when no sub-view is active.
//
// Props:
//   showAddMeeting       - boolean; when true, render the add-meeting form
//   editingMeeting       - meeting being edited (truthy renders the edit form)
//   showPDFUpdater       - boolean; when true, render the PDF updater
//   showAddEvent         - boolean; when true, render the add-event form
//   newEventType         - string event type passed to AddEventForm
//   editedTrip           - the current edited-trip working copy passed to children
//   onAddMeeting         - (meeting) => void for the add-meeting form
//   onEditMeeting        - (meeting) => void for the edit-meeting form
//   onPDFUpload          - (trip) => void for the PDF updater's onTripUpdated
//   onAddManualEvent     - (eventData) => void for the add-event form
//   onCancelAddMeeting   - () => void for the add-meeting form's onCancel
//   onCancelEditMeeting  - () => void for the edit-meeting form's onCancel
//   onClosePDFUpdater    - () => void for the PDF updater's onClose
//   onCancelAddEvent     - () => void for the add-event form's onCancel
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import AddMeetingForm from '../../AddMeetingForm';
import AddEventForm from '../../AddEventForm';
import TripPDFUpdater from '../../TripPDFUpdater';

export const SubViewRouter = React.memo(function SubViewRouter({
  showAddMeeting,
  editingMeeting,
  showPDFUpdater,
  showAddEvent,
  newEventType,
  editedTrip,
  onAddMeeting,
  onEditMeeting,
  onPDFUpload,
  onAddManualEvent,
  onCancelAddMeeting,
  onCancelEditMeeting,
  onClosePDFUpdater,
  onCancelAddEvent
}) {
  if (showAddMeeting) {
    return (
      <AddMeetingForm
        trip={editedTrip}
        onAddMeeting={onAddMeeting}
        onCancel={onCancelAddMeeting}
      />
    );
  }
  if (editingMeeting) {
    return (
      <AddMeetingForm
        trip={editedTrip}
        existingMeeting={editingMeeting}
        isEditing={true}
        onAddMeeting={onEditMeeting}
        onCancel={onCancelEditMeeting}
      />
    );
  }
  if (showPDFUpdater) {
    return (
      <TripPDFUpdater
        trip={editedTrip}
        onTripUpdated={onPDFUpload}
        onClose={onClosePDFUpdater}
      />
    );
  }
  if (showAddEvent) {
    return (
      <AddEventForm
        eventType={newEventType}
        trip={editedTrip}
        onAddEvent={onAddManualEvent}
        onCancel={onCancelAddEvent}
      />
    );
  }
  return null;
});

export default SubViewRouter;
